import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/** shadcn helper: combineer en dedup Tailwind classnames. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Formatteer bedrag in Nederlandse euro notatie. */
export function formatEuro(bedrag: number | string): string {
  const nummer = typeof bedrag === "string" ? Number(bedrag) : bedrag;
  if (Number.isNaN(nummer)) return "€ —";
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(nummer);
}

/** Formatteer datum als "15 apr 2026" — expliciet in Europe/Amsterdam.
 *
 * Zonder timeZone pakt Intl de runtime-tijdzone: op een UTC-runtime
 * rolt een UTC-tijdstip van na 22:00 (zomertijd) dan een dag terug
 * ("21 jul" waar de Orders-tab 22 jul zegt — natest 22-07). Kale
 * "YYYY-MM-DD"-strings parsen als UTC-middernacht en blijven met deze
 * timeZone gewoon de opgegeven dag. */
export function formatDatum(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const datum = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return "—";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Amsterdam",
  }).format(datum);
}

/** Formatteer datum + tijd als "15 apr 2026, 14:32" — expliciet in Europe/Amsterdam. */
export function formatDatumTijd(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const datum = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return "—";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  }).format(datum);
}

/** Landcode voluit in het Nederlands ("NL" → "Nederland"). */
export function landVoluit(code: string | null | undefined): string {
  if (!code) return "—";
  try {
    const namen = new Intl.DisplayNames(["nl"], { type: "region" });
    return namen.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/** Taalcode voluit in het Nederlands ("nl" → "Nederlands"). */
export function taalVoluit(code: string | null | undefined): string {
  if (!code) return "—";
  try {
    const namen = new Intl.DisplayNames(["nl"], { type: "language" });
    const naam = namen.of(code.toLowerCase()) ?? code;
    return naam.charAt(0).toUpperCase() + naam.slice(1);
  } catch {
    return code;
  }
}

/** Relatieve datumlabel ("3 dagen geleden"). */
/** Tijdweergave mail-lijst (JP-besluit 02-08, referentie Trengo):
 *  vandaag alleen de tijd ("14:32"), ouder datum én tijd
 *  ("28 jul 09:15", met jaartal zodra het een ander jaar is). */
/** Leesbare bestandsgrootte voor bijlage-chips (mail-leeskolom 02-08). */
export function formatBytes(b: number | null): string {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export function formatLijstTijd(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const datum = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return "—";
  const nu = new Date();
  const tijd = datum.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const zelfdeDag =
    datum.getDate() === nu.getDate() &&
    datum.getMonth() === nu.getMonth() &&
    datum.getFullYear() === nu.getFullYear();
  if (zelfdeDag) return tijd;
  const opties: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  if (datum.getFullYear() !== nu.getFullYear()) opties.year = "numeric";
  return `${datum.toLocaleDateString("nl-NL", opties)} ${tijd}`;
}

/** Compacte wachttijd voor het Aandacht nodig-blok: "26 min", "6 uur",
 *  "3 dagen" (mock-dashboard-v2 — zonder "geleden"). */
export function formatWachtDuur(
  iso: string | Date | null | undefined,
  nu: Date | number = Date.now(),
): string {
  if (!iso) return "—";
  const datum = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return "—";
  const nuMs = typeof nu === "number" ? nu : nu.getTime();
  const minuten = Math.max(0, Math.floor((nuMs - datum.getTime()) / 60000));
  if (minuten < 60) return `${minuten} min`;
  const uren = Math.floor(minuten / 60);
  if (uren < 24) return uren === 1 ? "1 uur" : `${uren} uur`;
  const dagen = Math.floor(uren / 24);
  return dagen === 1 ? "1 dag" : `${dagen} dagen`;
}

/** Deadline-label: "vandaag", "morgen", anders "12 aug" — en "verlopen"
 *  zodra de deadline voorbij is (Aandacht nodig + afsprakenblok). */
export function formatDeadline(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const datum = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return "—";
  const dagVan = (d: Date) =>
    new Intl.DateTimeFormat("nl-NL", {
      dateStyle: "short",
      timeZone: "Europe/Amsterdam",
    }).format(d);
  const nu = new Date();
  if (datum.getTime() < nu.getTime() && dagVan(datum) !== dagVan(nu)) {
    return "verlopen";
  }
  if (dagVan(datum) === dagVan(nu)) return "vandaag";
  const morgen = new Date(nu.getTime() + 86400000);
  if (dagVan(datum) === dagVan(morgen)) return "morgen";
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "short",
    timeZone: "Europe/Amsterdam",
  }).format(datum);
}

/** Seconden compact: "45 s", "38 min", "1:42" is bewust niet hier — de
 *  gespreksduur op het dashboard gebruikt formatMinSec. */
export function formatDuurCompact(seconden: number | null | undefined): string {
  if (seconden == null) return "—";
  if (seconden < 90) return `${Math.round(seconden)} s`;
  const minuten = seconden / 60;
  if (minuten < 90) return `${Math.round(minuten)} min`;
  const uren = minuten / 60;
  if (uren < 48) {
    return `${uren.toLocaleString("nl-NL", { maximumFractionDigits: 1 })} u`;
  }
  const dagen = uren / 24;
  return `${dagen.toLocaleString("nl-NL", { maximumFractionDigits: 1 })} d`;
}

/** Gespreksduur "1:42" (minuten:seconden, mock-dashboard-v2). */
export function formatMinSec(seconden: number | null | undefined): string {
  if (seconden == null) return "—";
  const m = Math.floor(seconden / 60);
  const s = Math.round(seconden % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatRelatief(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const datum = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return "—";
  const dagen = Math.floor(
    (Date.now() - datum.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (dagen === 0) return "vandaag";
  if (dagen === 1) return "gisteren";
  if (dagen < 7) return `${dagen} dagen geleden`;
  if (dagen < 30) return `${Math.floor(dagen / 7)} weken geleden`;
  if (dagen < 365) return `${Math.floor(dagen / 30)} maanden geleden`;
  return `${Math.floor(dagen / 365)} jaar geleden`;
}

/**
 * Genereer initialen voor een naam. Slaat Nederlandse tussenvoegsels
 * (van, de, der, etc.) over bij de achternaam.
 */
export function initialen(voornaam?: string | null, achternaam?: string | null): string {
  const voor = (voornaam ?? "").trim()[0] ?? "";
  const achter = (achternaam ?? "").trim();
  const tussenvoegsels = new Set([
    "de", "den", "der", "van", "ten", "ter", "te", "het", "op", "in", "aan",
  ]);
  const delen = achter.split(/\s+/).filter(Boolean);
  const hoofddeel =
    delen.find((d) => !tussenvoegsels.has(d.toLowerCase())) ?? delen[0] ?? "";
  const achterLetter = hoofddeel[0] ?? "";
  return (voor + achterLetter).toUpperCase();
}

/** Genereer een deterministische kleur uit een tekst (bijv. naam). */
const AVATAR_PALET = [
  { bg: "#e5f5fc", fg: "#2b9dc7" }, // VoerOnline cyaan
  { bg: "#ffedd5", fg: "#b35500" }, // VoerOnline oranje
  { bg: "#ede9fe", fg: "#6d28d9" }, // violet
  { bg: "#fef3c7", fg: "#b45309" }, // amber
  { bg: "#fce7f3", fg: "#be185d" }, // roze
  { bg: "#e6ecf3", fg: "#152e4f" }, // donkerblauw
];

export function avatarKleur(bron: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < bron.length; i++) {
    hash = (hash * 31 + bron.charCodeAt(i)) | 0;
  }
  return AVATAR_PALET[Math.abs(hash) % AVATAR_PALET.length];
}

/** VoerOnline begroetingsstandaard. */
export function begroeting(voornaam?: string | null, nu: Date = new Date()): string {
  const uur = nu.getHours();
  let woord = "Goedenavond";
  if (uur >= 6 && uur < 12) woord = "Goedemorgen";
  else if (uur >= 12 && uur < 18) woord = "Goedemiddag";
  return voornaam ? `${woord} ${voornaam}` : woord;
}
